import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { CallQueueDevice } from '../components/call-queue/types';

export interface UseCallQueueBatchedStateResult {
  devices: CallQueueDevice[];
  setDevices: Dispatch<SetStateAction<CallQueueDevice[]>>;
  initialDevices: CallQueueDevice[];
  resetDevices: (newDevices: CallQueueDevice[]) => void;
  hasDeviceChanges: boolean;
  handleRemoveDevice: (sipProfileId: string) => void;
  handleDevicesAssigned: (newDevices: CallQueueDevice[]) => void;
}

/**
 * Shared device state and handlers for the batched call queue save flow
 * used by both the full-page call queue settings view and the call-route step tray editor.
 *
 * Devices are tracked separately from the rest of the call queue form fields
 * because the call queue form schema (name, routing, caller experience, etc.)
 * is already managed by `useForm` with `computeChangedValues`. This hook only
 * needs to defer device add/remove until the save button is clicked.
 *
 * @param sourceDevices The latest known-good devices from the server (e.g. `data.devices`).
 * The hook re-seeds its local state from this value whenever it changes by reference.
 */
export const useCallQueueBatchedState = (sourceDevices: CallQueueDevice[]): UseCallQueueBatchedStateResult => {
  const [devices, setDevices] = useState<CallQueueDevice[]>(sourceDevices);
  const [initialDevices, setInitialDevices] = useState<CallQueueDevice[]>(sourceDevices);

  const resetDevices = useCallback((newDevices: CallQueueDevice[]) => {
    setDevices(newDevices);
    setInitialDevices(newDevices);
  }, []);

  // Re-seed whenever the source (server) devices change - e.g. a query refetch/invalidation.
  useEffect(() => {
    resetDevices(sourceDevices);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceDevices]);

  const hasDeviceChanges = useMemo(() => {
    // Compare by sipProfileId only (order-independent). Devices re-selected in the assign
    // modal come from the available-devices list (a different data source/shape than the
    // call queue's initial `devices`), so comparing full object equality would report a
    // change even when the set of assigned devices is actually unchanged.
    const currentIds = devices.map((device) => device.sipProfileId).sort();
    const initialIds = initialDevices.map((device) => device.sipProfileId).sort();
    if (currentIds.length !== initialIds.length) {
      return true;
    }
    return currentIds.some((id, index) => id !== initialIds[index]);
  }, [devices, initialDevices]);

  const handleRemoveDevice = useCallback((sipProfileId: string) => {
    setDevices((prevDevices) => prevDevices.filter((device) => device.sipProfileId !== sipProfileId));
  }, []);

  const handleDevicesAssigned = useCallback((newDevices: CallQueueDevice[]) => {
    setDevices(newDevices);
  }, []);

  return {
    devices,
    setDevices,
    initialDevices,
    resetDevices,
    hasDeviceChanges,
    handleRemoveDevice,
    handleDevicesAssigned,
  };
};
